import { Template } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown,
  Star,
  StarOff,
  Copy,
  Sparkles,
  Lock,
  Clock,
  Tag,
  Eye,
  BarChart
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TemplateDetailsDialogProps {
  template: Template | null;
  isOpen: boolean;
  onClose: () => void;
  onUse: (template: Template) => void;
  onToggleFavorite: (templateId: string) => void;
  isFavorite: boolean;
}

export function TemplateDetailsDialog({
  template,
  isOpen,
  onClose,
  onUse,
  onToggleFavorite,
  isFavorite,
}: TemplateDetailsDialogProps) {
  if (!template) return null;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(template.template_text);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 overflow-hidden bg-gradient-to-br from-white to-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative"
        >
          {/* Header Section */}
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  {template.title}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  {template.description}
                </DialogDescription>
              </div>
              {template.is_premium && (
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              )}
            </div>
          </DialogHeader>

          {/* Content Section */}
          <ScrollArea className="p-6 max-h-[60vh]">
            {/* Tags Section */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Tag className="w-4 h-4 text-gray-500" />
              {template.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                <div className="flex items-center text-gray-600 mb-1">
                  <Eye className="w-4 h-4 mr-1" />
                  Views
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {template.views_count.toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                <div className="flex items-center text-gray-600 mb-1">
                  <BarChart className="w-4 h-4 mr-1" />
                  Usage
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {template.usage_count.toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                <div className="flex items-center text-gray-600 mb-1">
                  <Clock className="w-4 h-4 mr-1" />
                  Created
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {new Date(template.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Template Text Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <div className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                {template.template_text}
              </div>
            </div>

            {/* Usage Instructions */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="text-blue-800 font-semibold mb-2">How to Use</h4>
              <ul className="text-blue-700 text-sm space-y-2">
                <li>• Replace [bracketed] text with your specific details</li>
                <li>• Follow the numbered steps in sequence</li>
                <li>• Customize the tone and style as needed</li>
                <li>• Add relevant context for better results</li>
              </ul>
            </div>
          </ScrollArea>

          {/* Actions Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-white border-t border-gray-100 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onToggleFavorite(template.id)}
                className={cn(
                  "transition-all duration-200",
                  isFavorite && "text-yellow-600 border-yellow-200 bg-yellow-50 hover:bg-yellow-100"
                )}
              >
                {isFavorite ? (
                  <>
                    <Star className="w-4 h-4 mr-2 fill-yellow-500" />
                    Favorited
                  </>
                ) : (
                  <>
                    <StarOff className="w-4 h-4 mr-2" />
                    Add to Favorites
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Text
              </Button>
            </div>
            <Button
              onClick={() => onUse(template)}
              className={cn(
                "transition-all duration-200",
                template.is_premium
                  ? "bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
                  : "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              )}
              disabled={template.is_premium}
            >
              {template.is_premium ? (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Premium Template
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Use Template
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
} 